import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import toast from "react-hot-toast"
import { useParams } from "react-router-dom"
import useAuth from "../hook/useAuth"
import useFormatNumber from "../hook/useFormatNumber"
import useFormatParams from "../hook/useFormatParams"
import { useSocket } from "../hook/useSocket"
import useUpdateFaviconBadge from "../hook/useUpdateFaviconBadge"
import apiServer from "../services/apiServer"
import { i18n } from "../translations/i18n"
const SocketContext = createContext(null)

export const SocketProvider = ({ children }) => {
  const [connectionId, setConnectionId] = useState(null)
  const [contact, setContact] = useState(null)
  const [messages, setMessages] = useState([])
  const [pagination, setPagination] = useState({ contacts: {}, contact: {} })
  const [messagesOrigin, setMessagesOrigin] = useState([])
  const [online, setOnline] = useState(true)
  const [allowScroll, setAllowScroll] = useState(false)
  const [connections, setConnections] = useState([])
  const [contacts, setContacts] = useState([])
  const [contactsOrigin, setContactsOrigin] = useState([])
  const [loading, setLoading] = useState({ contacts: true, chat: true })
  const [isListening, setIsListening] = useState(true)
  const [openModalQr, setOpenModalQr] = useState(null)
  const { formatParams } = useFormatParams()
  const { formatNumber } = useFormatNumber()
  const removedIds = useRef(new Set())
  const modalBlocked = useRef(false)
  const incomingRef = useRef([])
  const openedNotifiedRef = useRef(new Set())
  const { user } = useAuth()
  const userId = user?.id ?? ""
  const { id } = useParams()
  const { updateFavicon } = useUpdateFaviconBadge()

  const useSyncedRef = value => {
    const ref = useRef(value)
    ref.current = value
    return ref
  }

  const listeningRef = useSyncedRef(isListening)
  const dataModalRef = useSyncedRef(openModalQr)
  const connectionsRef = useSyncedRef(connections)
  const contactRef = useSyncedRef(contact)
  const contactsRef = useSyncedRef(contacts)

  const loadDataConnections = async () => {
    try {
      const result = await apiServer.get("/wbot/connections")
      const response = result?.data

      if (result?.status !== 200 || response?.error) {
        return console.error(response?.error ?? result)
      }
      const listConnectionsOrigin = response?.data?.connections ?? []
      const listConnectionsOriginFormatted = listConnectionsOrigin?.map(
        connect => {
          return { ...connect, qr: connect?.qrcode }
        }
      )

      setConnections(listConnectionsOriginFormatted)
    } catch (error) {
      console.error(error)
    }
  }

  const loadVerifyStatusConnection = () => {
    const listConnections = connectionsRef.current ?? []
    const currentConnection = formatNumber(id, true)

    if (!listConnections?.length || !currentConnection) return setOnline(false)

    const isCurrentConnectionOpen = listConnections?.some(
      c => c?.id === currentConnection && c?.status === "open"
    )

    setOnline(isCurrentConnectionOpen)
  }

  useEffect(() => {
    loadDataConnections()
  }, [])

  useEffect(() => {
    loadVerifyStatusConnection()
  }, [connections, connectionId])

  const toggleModal = connect => {
    if (!connect) {
      modalBlocked.current = false
      dataModalRef.current = null
      setOpenModalQr(undefined)

      return
    }

    dataModalRef.current = connect
    modalBlocked.current = true

    setOpenModalQr(prev => {
      const isEquals =
        (prev?.id ?? prev?.connectionId) ===
        (connect?.id ?? connect?.connectionId)
      const connectCurrent =
        connectionsRef.current.find(cn => cn?.id === connect?.connectionId) ??
        connect

      if (isEquals) return null

      return { ...connectCurrent, ...connect }
    })
  }

  const syncDataConnections = (prev = [], status = {}) => {
    const connectionId = status?.connectionId ?? status?.id

    if (!connectionId) return prev

    const matches = item =>
      String(item?.id ?? item?.connectionId) === String(connectionId)

    const index = prev.findIndex(matches)

    if (index !== -1) {
      if (status.statusCode === 401) {
        return prev.filter((_, i) => i !== index)
      }

      return prev.map((item, i) =>
        i === index ? { ...item, ...status } : item
      )
    }

    if (status.statusCode === 401) {
      return prev
    }

    return [...prev, { ...status }]
  }

  const scrollToBottom = elementId => {
    const container = document.getElementById(elementId ?? "messages-container")
    if (!container) return

    container.scrollTo({
      top: container.scrollHeight,
      behavior: "smooth"
    })

    setTimeout(() => {
      setAllowScroll(true)
    }, 200)
  }

  const appendUniqueById = (prev = [], incoming = []) => {
    if (!Array.isArray(prev) || !Array.isArray(incoming)) return prev

    const existingIds = new Set(prev.map(item => item?.id))
    const uniqueItems = incoming.filter(item => !existingIds.has(item?.id))

    return [...prev, ...uniqueItems]
  }

  const computeUnreadMap = () => {
    const map = new Map()
    const incoming = incomingRef.current ?? []
    for (const it of incoming) {
      const key = String(it.contactId ?? "")
      if (!key) continue
      map.set(key, (map.get(key) || 0) + 1)
    }
    return map
  }

  const computeTotalUnread = () => {
    let total = 0
    const incoming = incomingRef.current ?? []
    for (const it of incoming) total += 1
    return total
  }

  const loadDataContacts = useCallback(
    async ({ page, limit, connectionId } = { limit: 20 }) => {
      if (!connectionId) return

      try {
        const { paramsFormatted } = formatParams({ page, limit, connectionId })
        const result = await apiServer.get(
          `/wbot/connection-contacts?${paramsFormatted}`
        )
        const response = result?.data

        if (result?.status !== 200 || response?.error) {
          return console.error(response?.error ?? result)
        }
        const unreadMap = computeUnreadMap()

        const list = response?.data?.contacts ?? []
        const incoming = incomingRef.current ?? []
        const listFormatted = list?.map(ct => {
          const id = String(ct?.id)
          const count = unreadMap?.get(id) || 1
          const isNewContact = incoming?.some(
            item => item?.contactId === ct?.id
          )
          const lastMessage = ct?.lastMessage ?? {}
          const hasNewMessage = incoming?.some(
            item => item?.id === lastMessage?.id
          )

          if (isNewContact) {
            incomingRef.current = incoming.filter(i => i?.contactId !== ct?.id)
            return { ...ct, bullet: true, bulletCount: count }
          }

          if (hasNewMessage) {
            incomingRef.current = incoming.filter(
              i => i?.id !== lastMessage?.id
            )
            return { ...ct, bullet: true, bulletCount: count }
          }

          return ct
        })
        const currentPage = response?.data?.currentPage ?? 1
        const totalPages = response?.data?.totalPages ?? 1
        const totalContacts = response?.data?.totalContacts ?? 0

        setContacts(prev => appendUniqueById(prev, listFormatted))
        setContactsOrigin(prev => appendUniqueById(prev, listFormatted))

        setPagination(prev => ({
          ...prev,
          contacts: {
            currentPage,
            total: totalContacts,
            totalPages
          }
        }))
      } catch (error) {
        console.error(error)
        setOnline(false)
        console.error(error)
      } finally {
        setLoading(prev => ({ ...prev, contacts: false }))
      }
    },
    [connectionId, loading?.contacts]
  )

  const handlerIncludeMessage = msg => {
    const contactId = msg?.contactId
    const listContacts = contactsRef.current ?? []
    const existContact = listContacts.find(c => c?.id === contactId)
    const isFromMe = !!msg?.fromMe || !!msg?.userMessage
    const isOpenContact = contactId === contactRef.current?.id
    const showBullet = !isFromMe && !isOpenContact

    incomingRef.current = showBullet
      ? [
          ...(incomingRef.current ?? []),
          { id: msg?.id, type: existContact ? "message" : "contact", contactId }
        ]
      : []

    if (!existContact) {
      loadDataContacts({ connectionId })
      return
    }

    const listContactsUpdated = listContacts?.map(ct => {
      const isContact = ct?.id === contactId
      if (!isContact) return ct

      const lastMessage = { ...(ct?.lastMessage ?? {}), ...msg }
      const lastMessageAt = new Date().toISOString()

      if (!showBullet) {
        return {
          ...ct,
          lastMessage,
          lastMessageAt
        }
      }

      return {
        ...ct,
        lastMessage,
        lastMessageAt,
        bullet: true,
        bulletCount: (ct?.bulletCount || 0) + 1
      }
    })
    const updatedContact = listContactsUpdated.find(c => c?.id === contactId)
    const otherContacts = listContactsUpdated.filter(c => c?.id !== contactId)
    const reorderedContacts = updatedContact
      ? [updatedContact, ...otherContacts]
      : listContactsUpdated

    setMessages(prev => {
      const exists =
        prev.some(m => m?.id === msg?.id) ||
        msg?.contactId !== contactRef.current?.id
      if (exists) return prev

      return [...prev, msg]
    })
    setContacts(reorderedContacts)
    if (contactsRef && typeof contactsRef === "object") {
      contactsRef.current = reorderedContacts
    }

    if (showBullet) updateFavicon(computeTotalUnread())

    setTimeout(() => scrollToBottom(), 200)
  }

  const handleConnectionsStatus = data => {
    const isOpen = data?.status === "open"
    const isError = data?.status === "error" && !modalBlocked.current
    const id = data?.id ?? data?.connectionId
    const isOwner = data?.userId === userId

    if (isOpen && isOwner) {
      if (!openedNotifiedRef.current.has(id)) {
        openedNotifiedRef.current.add(id)

        toast.success(
          i18n.t("pages.whatsapp.connections.labels.success.connected")
        )
      }
    }

    if (isError && isOwner) {
      toast.error(i18n.t("pages.whatsapp.connections.labels.error.connected"))
    }

    if (removedIds.current.has(data?.connectionId) || modalBlocked.current) {
      return toggleModal(null)
    }

    const isPendingQr = data?.status === "connecting" && !!data?.qr

    if (isPendingQr && isOwner) {
      toggleModal(data)
    }

    setConnections(prevConnections =>
      syncDataConnections(
        prevConnections,
        isOwner ? data : { ...data, qr: null }
      )
    )
  }

  useSocket(
    {
      onConnectionStatus: handleConnectionsStatus,
      onConnected: () => {},
      onNewMessage: handlerIncludeMessage,
      onError: err => {
        console.error(err)
      }
    },
    {
      listeningRef,
      setIsListening,
      connectionId
    }
  )

  const value = useMemo(
    () => ({
      connectionId,
      connections,
      allowScroll,
      openModalQr,
      dataModalRef,
      messages,
      contact,
      contacts,
      messagesOrigin,
      loading,
      pagination,
      contactsOrigin,
      online,
      setPagination,
      setOnline,
      setLoading,
      setConnections,
      setMessagesOrigin,
      setContacts,
      setContact,
      toggleModal,
      setConnectionId,
      setIsListening,
      setMessages,
      scrollToBottom,
      loadDataConnections,
      onIncludeMessage: handlerIncludeMessage,
      updateFavicon: () => updateFavicon(0),
      getConnection: id => connections?.find(c => c?.connectionId === id),
      onLoadContacts: loadDataContacts
    }),
    [
      connections,
      openModalQr,
      connectionId,
      messages,
      allowScroll,
      contact,
      contacts,
      loading,
      pagination,
      contactsOrigin,
      online
    ]
  )

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  )
}

export const useSocketContext = () => {
  const context = useContext(SocketContext)
  if (!context)
    throw new Error(
      "useSocketContext deve ser usado dentro de um SocketProvider"
    )
  return context
}
